import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";

const TARGET_RANGE_HALF_WIDTH = 0.25;
const LOCKED_TICK_INTERVAL = 0.02;
// When true, show a coloured line per jag driver
const SHOW_DRIVER_TARGET_LINES = false;

export default function Caterpillar({ carColour }: { carColour: string }) {
  const raceConfig = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig,
  );

  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  const raceData = useSelector((state: RootState) => state.raceData, isEqual);

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const target = raceConfig?.targetRace ?? 0;

  const pelotonTarget = strategyImport?.ELapTargetLockedPeloton ?? null;
  const leaderTarget = strategyImport?.ELapTargetLockedLeader ?? null;

  // Incase leader locking is ever lower than peleton locking
  const lockedMin =
    pelotonTarget !== null && leaderTarget !== null
      ? Math.min(pelotonTarget, leaderTarget)
      : null;
  const lockedMax =
    pelotonTarget !== null && leaderTarget !== null
      ? Math.max(pelotonTarget, leaderTarget)
      : null;

  const min = target - TARGET_RANGE_HALF_WIDTH;
  const max = target + TARGET_RANGE_HALF_WIDTH;

  const getPercent = (value: number) => ((value - min) / (max - min)) * 100;

  const displayedLockedRange = useMemo(() => {
    if (lockedMin === null || lockedMax === null) return null;
    if (lockedMax < min || lockedMin > max) return null;
    return {
      min: Math.max(lockedMin, min),
      max: Math.min(lockedMax, max),
    };
  }, [lockedMin, lockedMax, min, max]);

  const lockedTicks = useMemo(() => {
    if (
      lockedMin === null ||
      lockedMax === null ||
      displayedLockedRange === null
    )
      return [];
    const steps = Math.round((lockedMax - lockedMin) / LOCKED_TICK_INTERVAL);
    const ticks = Array.from(
      { length: Math.max(steps - 1, 0) },
      (_, i) => lockedMin + (i + 1) * LOCKED_TICK_INTERVAL,
    );
    return ticks.filter(
      (tick) =>
        tick >= displayedLockedRange.min && tick <= displayedLockedRange.max,
    );
  }, [lockedMin, lockedMax, displayedLockedRange]);

  // Every other jag driver's target, coloured by their driver highlight colour, hidden if outside the visible range.
  const driverTargetLines = useMemo(() => {
    if (!SHOW_DRIVER_TARGET_LINES) return [];
    return Object.entries(jagDrivers)
      .filter(([driverCarColour]) => driverCarColour !== carColour)
      .map(([driverCarColour, driverNumber]) => {
        const driverTarget =
          raceData[driverCarColour]?.raceConfig?.targetRace ?? 0;
        const driver = driverInfo[driverNumber];
        return {
          driverCarColour,
          driverColour: driver?.driverHighlightColourAlt || "#888",
          target: driverTarget,
        };
      })
      .filter(
        (row): row is typeof row & { target: number } =>
          row.target !== null && row.target >= min && row.target <= max,
      );
  }, [carColour, driverInfo, jagDrivers, raceData, min, max]);

  // Own car's driver colour, used for the centre line when driver target lines are shown.
  const ownDriverColour = useMemo(() => {
    const driverNumber = jagDrivers[carColour];
    if (!driverNumber) return null;
    return driverInfo[driverNumber]?.driverHighlightColourAlt ?? null;
  }, [carColour, driverInfo, jagDrivers]);

  return (
    <div className={styles.container}>
      <div className={styles.trackContainer}>
        <div className={styles.track}>
          {SHOW_DRIVER_TARGET_LINES &&
            driverTargetLines.map((row) => (
              <div
                key={row.driverCarColour}
                className={styles.driverTargetLine}
                style={{
                  left: `${getPercent(row.target)}%`,
                  backgroundColor: row.driverColour,
                }}
              />
            ))}
          {displayedLockedRange !== null && (
            <div
              className={styles.lockedRangeBox}
              style={{
                left: `${getPercent(displayedLockedRange.min)}%`,
                width: `${
                  getPercent(displayedLockedRange.max) -
                  getPercent(displayedLockedRange.min)
                }%`,
              }}
            >
              {lockedTicks.map((tickValue) => (
                <div
                  key={tickValue}
                  className={styles.lockedTick}
                  style={{
                    left: `${
                      ((tickValue - displayedLockedRange.min) /
                        (displayedLockedRange.max - displayedLockedRange.min)) *
                      100
                    }%`,
                  }}
                />
              ))}
            </div>
          )}
          <div
            className={styles.targetLine}
            style={{
              left: "50%",
              backgroundColor:
                SHOW_DRIVER_TARGET_LINES && ownDriverColour
                  ? ownDriverColour
                  : undefined,
            }}
          />
        </div>
      </div>
    </div>
  );
}
